import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { FileItem, FileService } from '@app/core/services/file.service';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { ModalService } from '@app/core/services/modal.service';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { StateService } from '@app/core/services/state.service';
import { GridRow } from '@app/core/components/grid/grid.types';
import { DocPackTypeFormatStyleGet } from '@app/services/doc-pack-type.service';
import { RowSelectedEvent } from '@ag-grid-community/core';
import { MergeDocument, PdfService } from '@app/services/pdf.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
    selector: 'tt-doc-pack-chapter',
    templateUrl: './doc-pack-chapter.component.html',
    styleUrls: ['./doc-pack-chapter.component.css'],
})
export class DocPackChapterComponent extends ComponentBaseComponent implements OnChanges, OnDestroy {
    @Input()
    public params?: GetDocPackChapterParams;

    @Input()
    public styling?: DocPackTypeFormatStyleGet;

    public get?: DocPackChapterGet;

    public chapterDocumentsGrid?: GridOptions<DocPackChapterDocumentRow>;

    public htmlProcessing = false;

    public saving = false;

    public resetting = false;

    public selectedDocument?: FileItem;

    private selectedDocumentLoadId = 0;

    public selectedDocumentLoading = false;

    public selectedDocumentRotation = 0;

    public selectedDocumentRotatable = false;

    public selectedDocumentPreviewSrc?: SafeResourceUrl;

    public selectedDocumentImageSrc?: string;

    public selectedDocumentIsImage = false;

    public rotatingDocument = false;

    private selectedDocumentRow?: DocPackChapterDocumentRow;

    private selectedDocumentContent?: ArrayBuffer;

    private selectedDocumentContentType?: MergeDocument['contentType'];

    private selectedDocumentPreviewUrl?: string;

    constructor(
        private datatask: DataTaskService,
        private state: StateService,
        private file: FileService,
        private pdf: PdfService,
        private sanitizer: DomSanitizer,
        public modal: ModalService,
    ) {
        super();
    }

    public onChapterDocumentsGridRead() {
        const gridfunc = this.chapterDocumentsGrid?.gridfunc;

        if ((gridfunc?.getRows().length ?? 0) === 0) {
            this.clearSelectedDocument();
            return;
        }

        if ((gridfunc?.getGridApi().getSelectedRows().length ?? 0) === 0) gridfunc?.selectRow(0);
    }

    public onRowSelected(event: RowSelectedEvent<DocPackChapterDocumentRow>) {
        if (!event.node.isSelected()) return;

        this.loadSelectedDocument(event.data);
    }

    public async rotateSelectedDocument(rotation: -90 | 90) {
        if (!this.selectedDocumentRow || !this.selectedDocumentContentType || this.rotatingDocument) return;
        if (this.selectedDocumentContentType === 'application/pdf' && !this.selectedDocumentContent) return;

        this.rotatingDocument = true;
        this.selectedDocumentLoading = this.selectedDocumentContentType === 'application/pdf';
        this.selectedDocumentRotation = this.normalizeRotation(this.selectedDocumentRotation + rotation);
        const rotationDegrees: `${number}` = `${this.selectedDocumentRotation}`;

        try {
            const tasks = [this.saveSelectedDocumentRotation(this.selectedDocumentRow, rotationDegrees)];

            if (this.selectedDocumentContentType === 'application/pdf') tasks.push(this.renderRotatedPreview());

            const results = await Promise.allSettled(tasks);

            results.forEach((result) => {
                if (result.status === 'rejected') console.error(result.reason);
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.selectedDocumentLoading = false;
            this.rotatingDocument = false;
        }
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage } = await this.saveDocPackChapter({ ...this.get, html_text: this.base64UrlEncodeUnicode(this.get.html_text) });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async reset() {
        if (!this.get) return;

        try {
            this.resetting = true;

            const { errorcode, errormessage } = await this.resetDocPackChapter(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.init();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.resetting = false;
        }
    }

    private readChaptersDocumentsGrid() {
        if (!this.chapterDocumentsGrid) {
            this.chapterDocumentsGrid = new Grid<DocPackChapterDocumentRow>()
                .setRememberId('w_docpack_docpackchapteritem_grid')
                .setLoadDataFunction({ method: 3291, parameters: () => ({ docpackchapter_keyno_selected: this.params?.docpackchapter_keyno }) })
                .setKendoConfig({ selectable: 'single' })
                .addToolbarButton({
                    name: 'upload',
                    text: 'docpack_docpackchapter_upload',
                    icon: 'far fa-plus',
                    cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
                    func: async () => {
                        await this.modal.openStateDialog({ state: 'documents', params: `docpackchapter_keyno/${this.params?.docpackchapter_keyno}` });
                        this.readChaptersDocumentsGrid();
                    },
                })
                .addToolbarButton({ name: 'addchapter', text: 'docpack_docpackchapteritem_add', icon: 'far fa-plus', cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r', func: () => this.state.go('docpackdocadd', { docpackchapter_keyno: this.get?.docpackchapter_keyno }) })
                .addToolbarButton({ name: 'delete', text: 'docpack_docpackchapteritem_delete_selected', icon: 'fad fa-trash', cssClass: 'tt-button tt-button--danger im-grid-btn-xs-r', func: () => this.deleteSelectedChapters(), disable_if_no_selected: true });
        } else {
            this.chapterDocumentsGrid.gridfunc?.read(false);
        }
    }

    private async deleteSelectedChapters() {
        const selectedRows = this.chapterDocumentsGrid?.gridfunc?.getIsSelectedRows() ?? [];

        if (!this.get || selectedRows.length === 0) return;

        const deleteConfirmed = await this.modal.openConfirmDialog({ type: 'danger', title: 'confirm_title', translateTitle: true, message: 'confirm_message', translateMessage: true });

        if (!deleteConfirmed) return;

        try {
            const params = { ...this.get, records: selectedRows };

            const { errorcode, errormessage } = await this.deleteDocPackChapterDocuments(params);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.readChaptersDocumentsGrid();
        }
    }

    private async loadGet() {
        if (!!this.params) {
            const response = await this.getDocPackChapter(this.params);
            this.get = { ...response, html_text: this.base64UrlDecode(response.html_text) };
        }
    }

    private async loadSelectedDocument(row?: DocPackChapterDocumentRow) {
        const loadId = ++this.selectedDocumentLoadId;
        const filename = row?.docfilename?.trim() || row?.filename?.trim();

        this.clearSelectedDocumentPreview();
        this.selectedDocumentRow = row;
        this.selectedDocumentRotation = this.normalizeRotation(Number(row?.rotation_degrees ?? 0));
        this.selectedDocumentRotatable = false;
        this.selectedDocumentIsImage = false;
        delete this.selectedDocumentImageSrc;
        delete this.selectedDocumentContent;
        delete this.selectedDocumentContentType;

        if (!filename) {
            delete this.selectedDocument;
            return;
        }

        this.selectedDocumentLoading = true;

        try {
            const selectedDocument = await this.file.getFileDetails({ fileName: filename, maxWidth: 1200, maxHeight: 600 });

            if (loadId !== this.selectedDocumentLoadId) return;

            const contentType = this.getRotatableContentType(selectedDocument);

            if (contentType && contentType !== 'application/pdf') {
                const imageLink = selectedDocument.thumbnailLink || selectedDocument.imageLink;

                if (!imageLink) throw Error('Could not load image preview.');

                this.selectedDocumentContentType = contentType;
                this.selectedDocumentRotatable = true;
                this.selectedDocumentIsImage = true;
                this.selectedDocumentImageSrc = this.file.getTokenLink(imageLink);
                this.selectedDocument = selectedDocument;
            } else if (contentType === 'application/pdf' && selectedDocument.imageLink) {
                const response = await fetch(this.file.getTokenLink(selectedDocument.imageLink));

                if (!response.ok) throw Error(`Could not load document preview (${response.status}).`);

                const content = await response.arrayBuffer();

                if (loadId !== this.selectedDocumentLoadId) return;

                this.selectedDocumentContent = content;
                this.selectedDocumentContentType = contentType;
                this.selectedDocumentRotatable = true;
                this.selectedDocument = selectedDocument;
                await this.renderRotatedPreview();
            } else {
                this.selectedDocument = this.hidePdfViewerControls(selectedDocument);
            }
        } catch (error) {
            if (loadId === this.selectedDocumentLoadId) delete this.selectedDocument;
            console.error(error);
        } finally {
            if (loadId === this.selectedDocumentLoadId) this.selectedDocumentLoading = false;
        }
    }

    private async renderRotatedPreview() {
        if (!this.selectedDocument || !this.selectedDocumentContent || !this.selectedDocumentContentType) return;

        const pdf = await this.pdf.createRotatedPdf(this.selectedDocumentContent, this.selectedDocumentContentType, this.selectedDocumentRotation);
        const pdfBuffer = new ArrayBuffer(pdf.byteLength);
        new Uint8Array(pdfBuffer).set(pdf);
        const previewUrl = URL.createObjectURL(new Blob([pdfBuffer], { type: 'application/pdf' }));

        this.clearSelectedDocumentPreview();
        this.selectedDocumentPreviewUrl = previewUrl;
        this.selectedDocumentPreviewSrc = this.sanitizer.bypassSecurityTrustResourceUrl(`${previewUrl}#toolbar=0&navpanes=0`);
        this.selectedDocument = this.hidePdfViewerControls({ ...this.selectedDocument, contentType: 'application/pdf', fileName: 'preview.pdf', imageLink: previewUrl });
    }

    private getRotatableContentType(file: FileItem): MergeDocument['contentType'] | undefined {
        if (file.contentType === 'application/pdf' || file.fileName?.toLowerCase().endsWith('.pdf')) return 'application/pdf';
        if (file.contentType === 'image/jpeg' || file.contentType === 'image/jpg') return file.contentType;
        if (file.contentType === 'image/png') return 'image/png';

        return undefined;
    }

    private hidePdfViewerControls(file: FileItem): FileItem {
        const isPdf = file.contentType === 'application/pdf' || file.fileName?.toLowerCase().endsWith('.pdf');

        if (!isPdf || !file.imageLink) return file;

        const separator = file.imageLink.includes('#') ? '&' : '#';

        return { ...file, imageLink: `${file.imageLink}${separator}toolbar=0&navpanes=0` };
    }

    private clearSelectedDocument() {
        this.selectedDocumentLoadId++;
        this.clearSelectedDocumentPreview();
        delete this.selectedDocumentRow;
        delete this.selectedDocumentContent;
        delete this.selectedDocumentContentType;
        delete this.selectedDocument;
        this.selectedDocumentRotatable = false;
        this.selectedDocumentIsImage = false;
        delete this.selectedDocumentImageSrc;
        this.selectedDocumentLoading = false;
    }

    private clearSelectedDocumentPreview() {
        if (this.selectedDocumentPreviewUrl) URL.revokeObjectURL(this.selectedDocumentPreviewUrl);

        delete this.selectedDocumentPreviewUrl;
        delete this.selectedDocumentPreviewSrc;
    }

    private normalizeRotation(rotation: number): 0 | 90 | 180 | 270 {
        const normalizedRotation = ((rotation % 360) + 360) % 360;

        return [0, 90, 180, 270].includes(normalizedRotation) ? (normalizedRotation as 0 | 90 | 180 | 270) : 0;
    }

    private async saveSelectedDocumentRotation(row: DocPackChapterDocumentRow, rotationDegrees: `${number}`): Promise<void> {
        await this.datatask.Post(4046, {
            docpackchapteritem_keyno: row.docpackchapteritem_keyno,
            rotation_degrees: rotationDegrees,
        });

        const rowNode = this.chapterDocumentsGrid?.gridfunc?.getRowNode(row);
        const updatedRow = { ...(rowNode?.data ?? row), rotation_degrees: rotationDegrees } as DocPackChapterDocumentRow;

        if (rowNode) rowNode.updateData(updatedRow);
        else Object.assign(row, updatedRow);

        const isStillSelected =
            this.selectedDocumentRow === row ||
            (!!row._uuid && this.selectedDocumentRow?._uuid === row._uuid) ||
            (!!row.docpackchapteritem_keyno && this.selectedDocumentRow?.docpackchapteritem_keyno === row.docpackchapteritem_keyno);

        if (isStillSelected) this.selectedDocumentRow = updatedRow;
    }

    private async getDocPackChapter(params: GetDocPackChapterParams): Promise<DocPackChapterGet> {
        return (await this.datatask.Post(3311, params))[0];
    }

    private async saveDocPackChapter(params: DocPackChapterGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3312, params))[0];
    }

    private async resetDocPackChapter(params: DocPackChapterGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3315, params))[0];
    }

    private async deleteDocPackChapterDocuments(params: DeleteDocPackChapterDocumentsParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3292, params))[0];
    }

    private async init() {
        delete this.get;
        this.clearSelectedDocument();
        this.readChaptersDocumentsGrid();
        await this.loadGet();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.clearSelectedDocumentPreview();
    }
}

export interface GetDocPackChapterParams {
    docpackchapter_keyno: string;
}

export interface DocPackChapterGet {
    docpackchapter_keyno: string;
    docpacktypechapter_order: string;
    docpacktypechapter_name: string;
    isrequired: '1' | '0';
    is_selected: '1' | '0';
    nbr_of_documents: `${number}`;
    html_text: string;
}

export interface DeleteDocPackChapterDocumentsParams extends DocPackChapterGet {
    records: GridRow[];
}

export type DocPackChapterDocumentRow = GridRow & {
    docpackchapteritem_keyno?: string;
    docfilename?: string;
    filename?: string;
    rotation_degrees?: `${number}`;
};
